import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiInfo, FiTrash2, FiPlus } from "react-icons/fi";
import { MdModeEditOutline } from "react-icons/md";
import {
  GenericTable,
  Spinner,
  Pagination,
  Tooltip,
  SearchableDropdown,
  RoleValidator,
} from "../../../Components/Common";
import {
  ADVANCE_NOTE_COLUMNS,
  ADVANCE_NOTE_LABELS,
  MISC,
  TOAST_MESSAGES,
  ADVANCE_NOTE_STATUS_OPTIONS,
  ADVANCE_NOTE_STATUS_LABELS,
  getAdvanceNoteStatusColor,
  ADVANCE_NOTE_TOOLTIP,
  ADVANCE_NOTE_STATUS,
} from "./constants";
import * as routes from "../../../routes";
import { useAdvanceNote } from "../Hooks/useAdvanceNote";
import { ROLES } from "../../../Constants/Strings";
import { formatDate } from "../../../Utils/timeUtils";
import { useProjects } from "../../Project/Hooks/useProjects";

const AdvanceNoteList = () => {
  const {
    loading,
    page,
    totalPages,
    rowsPerPage,
    filters,
    setFilters,
    onPageChange,
    onPageSizeChange,
    fetchAdvanceNotes,
    deleteAdvanceNoteData,
    fetchUsers,
    fetchSites,
    fetchCategoriesList,
    fetchProjectSites,
  } = useAdvanceNote();
  const [advanceNotes, setAdvanceNotes] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const loadAdvanceNotes = async () => {
    const data = await fetchAdvanceNotes();
    setAdvanceNotes(data || []);
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(TOAST_MESSAGES.ADV_NOTE_DELETE_ALERT);
    if (!confirmed) return;

    setDeleting(true);
    const success = await deleteAdvanceNoteData(id);
    if (success) loadAdvanceNotes();
    setDeleting(false);
  };

  const renderField = (key, row) => {
    switch (key) {
      case "user_name":
        return <span className="text-gray-800">{row.user_name || "—"}</span>;

      case "amount":
        return (
          <span className="font-medium text-gray-800">
            ₹{row.amount?.toLocaleString()}
          </span>
        );

      case "status":
        return (
          <span
            className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${getAdvanceNoteStatusColor(
              row.status
            )}`}
          >
            {ADVANCE_NOTE_STATUS_LABELS[row.status] || row.status}
          </span>
        );

      case "actions":
        return (
          <div className="flex justify-start gap-4 text-lg text-center">
            <Tooltip text={ADVANCE_NOTE_TOOLTIP.VIEW_TEXT}>
              <FiInfo
                className="cursor-pointer text-primary hover:text-secondary"
                onClick={() =>
                  navigate(
                    routes.VIEW_ADVANCE_NOTE.replace(":instanceId", row.id)
                  )
                }
              />
            </Tooltip>
            <Tooltip
              text={
                row.status === ADVANCE_NOTE_STATUS.DRAFT
                  ? ADVANCE_NOTE_TOOLTIP.EDIT_TEXT
                  : ADVANCE_NOTE_TOOLTIP.CAN_NOT_EDIT
              }
            >
              <RoleValidator allowedRoles={ROLES.PROJECT_MANAGER}>
                <button
                  disabled={row.status !== ADVANCE_NOTE_STATUS.DRAFT}
                  className={`${
                    row.status === ADVANCE_NOTE_STATUS.DRAFT
                      ? "text-primary hover:text-blue-700"
                      : "text-gray-400 cursor-not-allowed"
                  }`}
                  onClick={() =>
                    navigate(
                      routes.EDIT_ADVANCE_NOTE.replace(":instanceId", row.id)
                    )
                  }
                >
                  <MdModeEditOutline />
                </button>
              </RoleValidator>
            </Tooltip>
            <Tooltip
              text={
                row.status === ADVANCE_NOTE_STATUS.DRAFT
                  ? ADVANCE_NOTE_TOOLTIP.DELETE_TEXT
                  : ADVANCE_NOTE_TOOLTIP.CAN_NOT_DELETE
              }
            >
              <RoleValidator allowedRoles={ROLES.PROJECT_MANAGER}>
                <button
                  disabled={row.status !== ADVANCE_NOTE_STATUS.DRAFT}
                  className={`${
                    row.status === ADVANCE_NOTE_STATUS.DRAFT
                      ? "text-red-500 hover:text-red-700"
                      : "text-gray-400 cursor-not-allowed"
                  }`}
                  onClick={() => handleDelete(row.id)}
                >
                  <FiTrash2 />
                </button>
              </RoleValidator>
            </Tooltip>
          </div>
        );

      default:
        return (
          <span className={row[key] ? "text-gray-800" : "text-gray-400 italic"}>
            {row[key] || "-"}
          </span>
        );
    }
  };

  const renderMobileField = (row) => {
    return (
      <div
        key={row.id}
        onClick={() =>
          navigate(routes.VIEW_ADVANCE_NOTE.replace(":instanceId", row.id))
        }
        className="grid grid-cols-9 rounded-lg p-4 shadow-md border border-gray-300 bg-white hover:shadow-lg transition-shadow cursor-pointer mb-3"
      >
        <div className="col-span-4 flex flex-col justify-center gap-1">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {row.user_name || "User"}
          </span>

          <span className="text-xs text-gray-700 truncate mt-1">
            {row.site_name || MISC.NOT_AVAILABLE}
          </span>

          <span className="text-xs text-gray-700 truncate mt-1">
            {row.category_name || "Reimbursement"}
          </span>
        </div>

        <div className="col-span-2 flex flex-col items-center justify-end gap-2">
          <span className="text-base font-semibold text-gray-900 truncate">
            ₹{row.amount?.toLocaleString("en-IN")}
          </span>
          <span
            className={` text-[11px] px-2 py-0.5 rounded-full font-medium text-center ${getAdvanceNoteStatusColor(
              row.status
            )}`}
          >
            {ADVANCE_NOTE_STATUS_LABELS[row.status] || row.status}
          </span>
        </div>

        <div
          className="col-span-3 flex flex-col justify-between items-end gap-5"
          onClick={(e) => e.stopPropagation()}
        >
          <div>
            <span className="text-sm text-gray-600 font-medium mb-1 truncate">
            {formatDate(row.date)}
          </span>
          </div>
          <div className="flex flex-row justify-end items-center gap-3">
            <span>
            <Tooltip text={ADVANCE_NOTE_TOOLTIP.EDIT_TEXT}>
              <MdModeEditOutline
                size={18}
                className="cursor-pointer text-primary hover:text-blue-700"
                onClick={() =>
                  navigate(
                    routes.EDIT_ADVANCE_NOTE.replace(":instanceId", row.id)
                  )
                }
              />
            </Tooltip>
          </span>
          <span>
            <Tooltip text={ADVANCE_NOTE_TOOLTIP.DELETE_TEXT}>
              <FiTrash2
                size={18}
                className="cursor-pointer text-red-500 hover:text-red-700"
                onClick={() => handleDelete(row.id)}
              />
            </Tooltip>
          </span>
          </div>
          
          
        </div>
      </div>
    );
  };

  useEffect(() => {
    loadAdvanceNotes(page);
  }, [page, rowsPerPage, filters]);

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl font-semibold">Advance Notes</h2>
        <RoleValidator allowedRoles={ROLES.PROJECT_MANAGER}>
          <button
            onClick={() => navigate(routes.CREATE_ADVANCE_NOTE)}
            className="hidden md:flex items-center px-4 py-2 bg-primary text-white rounded-lg shadow hover:bg-secondary transition-colors"
          >
            <FiPlus className="inline mr-2" /> Add Advance Note
          </button>
        </RoleValidator>

        <RoleValidator allowedRoles={ROLES.PROJECT_MANAGER}>
          <button
            onClick={() => navigate(routes.CREATE_ADVANCE_NOTE)}
            className="flex md:hidden items-center justify-center px-4 py-2 bg-primary text-white rounded-md shadow hover:bg-secondary transition-colors"
          >
            <FiPlus className="text-lg" />
          </button>
        </RoleValidator>
      </div>

      <div className="bg-white w-full rounded-2xl shadow-md border px-4 py-3 mb-4">
        <div className="flex flex-wrap gap-4">
          <SearchableDropdown
            fetchFunction={fetchUsers}
            onSelect={(val) => setFilters({ ...filters, user: val })}
            valueKey="id"
            labelKey="first_name"
            includeAllOption
            allOptionLabel="All Users"
            placeholder="Select User"
            width="40"
          />

          <SearchableDropdown
            fetchFunction={fetchProjectSites}
            onSelect={(val) => setFilters({ ...filters, site__project: val })}
            valueKey="id"
            labelKey="name"
            includeAllOption
            allOptionLabel="All Projects"
            placeholder="Select Project"
            width="44"
          />

          <SearchableDropdown
            fetchFunction={fetchSites}
            onSelect={(val) => setFilters({ ...filters, site: val })}
            valueKey="id"
            labelKey="site_name"
            includeAllOption
            allOptionLabel="All Sites"
            placeholder="Select Site"
            width="44"
          />

          <SearchableDropdown
            fetchFunction={fetchCategoriesList}
            onSelect={(val) => setFilters({ ...filters, category: val })}
            valueKey="id"
            labelKey="name"
            includeAllOption
            allOptionLabel="All Categories"
            placeholder="Select Category"
            width="44"
          />

          <SearchableDropdown
            fetchFunction={async () => ADVANCE_NOTE_STATUS_OPTIONS}
            onSelect={(val) => setFilters({ ...filters, status: val })}
            valueKey="id"
            labelKey="name"
            placeholder="Select Status"
            width="40"
          />
        </div>
      </div>

      {loading && advanceNotes.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Spinner size={4} />
        </div>
      ) : advanceNotes.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          {MISC.NO_ADVANCE_NOTES_FOUND}
        </div>
      ) : (
        <>
          <div className="overflow-y-auto max-h-[50vh] pb-4 rounded-md relative">
            <GenericTable
              tableColumns={ADVANCE_NOTE_COLUMNS}
              tableRows={advanceNotes}
              columnLabels={ADVANCE_NOTE_LABELS}
              renderField={renderField}
              renderMobileCard={renderMobileField}
              mobileGridColumns={3}
            />
          </div>

          {deleting && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
              <Spinner />
            </div>
          )}

          <Pagination
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      )}
    </div>
  );
};

export default AdvanceNoteList;
